import React from "react";
import { motion } from "framer-motion";
import heroBg from "../assets/hero-01.png";
import visionIcon from "../assets/icon/vision.png";
import missionIcon from "../assets/icon/mission.png";
import serviceMan from "../assets/about-hero.png";
import whyImage from "../assets/why_choose_us.png";
import BrandPillars from "../components/BrandPillars";
import { Link } from "react-router-dom";

const visionMission = [
  {
    icon: visionIcon,
    title: "VISION",
    text: "To be East Africa's most trusted and technically excellent HVAC company, a name synonymous with engineering precision, energy efficiency, and unmatched client service.",
  },
  {
    icon: missionIcon,
    title: "MISSION",
    text: "To deliver reliable, high-quality HVAC design, installation, supply, and maintenance services that enhance comfort, improve energy efficiency, and create outstanding long-term value for every client.",
  },
];

const coreValues = [
  { value: "Technical Excellence",         means: "We bring engineering precision to every project. No guesswork — only proven, specification-grade solutions delivered consistently." },
  { value: "Integrity & Transparency",     means: "Honest assessments, clear pricing, no hidden costs. We say what we mean and deliver exactly what we promise." },
  { value: "Client-First Philosophy",      means: "Every decision is guided by what genuinely serves the client's best interest — not our convenience or profit margin." },
  { value: "Reliability & Accountability", means: "We show up, we deliver, and we take full ownership of our work — from first site visit to long after installation." },
  { value: "Innovation & Improvement",     means: "We continuously adopt modern technologies and best practices so our clients always benefit from current advancements." },
  { value: "Professionalism",              means: "In appearance, communication, and conduct — we represent a standard that builds lasting confidence and trust." },
];

const promises = [
  "Honest, detailed needs assessments before any work begins — no assumptions, no shortcuts.",
  "Fully transparent, itemized quotations — no hidden costs, no surprise charges at completion.",
  "After-sales support, scheduled follow-ups, and accessible technical assistance whenever needed.",
  "Genuine, warranty-backed equipment sourced only from authorized suppliers — never counterfeit or grey market products.",
];

export default function AboutPage() {
  return (
    <motion.div
      className="font-poppins"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >

      {/* ── HERO BANNER ── */}
      <section className="relative min-h-[300px] sm:min-h-[380px] lg:min-h-[450px] flex items-center justify-center text-white overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${heroBg})` }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 199, 239, 0.55)" }} />
        <div className="relative text-center px-4 sm:px-8 lg:px-16 py-14 sm:py-20">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" style={{ textShadow: "2px 2px 8px rgba(0,0,0,0.8)" }}>
            About Us
          </h1>
          <p className="text-base sm:text-xl lg:text-2xl font-semibold uppercase tracking-widest max-w-3xl mx-auto text-white leading-snug" style={{ textShadow: "1px 1px 6px rgba(0,0,0,0.9)" }}>
            We are your best HVAC service provider with excellent customer service & support
          </p>
        </div>
      </section>

      {/* ── VISION & MISSION — Vision from left, Mission from right ── */}
      <section className="bg-gray-50 py-10 sm:py-14 lg:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
          {visionMission.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, x: i === 0 ? -100 : 100 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false, amount: 0.4 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="rounded-lg overflow-hidden"
              style={{ border: "2px solid #00C7EF" }}
            >
              <div className="flex items-center gap-3 px-4 sm:px-5 py-3 sm:py-4" style={{ backgroundColor: "#0F1030" }}>
                <img src={item.icon} alt={item.title} className="w-7 h-7 sm:w-8 sm:h-8 object-contain" />
                <h3 className="text-lg sm:text-xl lg:text-2xl font-extrabold tracking-wider" style={{ color: "#00C7EF" }}>
                  {item.title}
                </h3>
              </div>
              <div className="bg-white px-4 sm:px-5 py-5 sm:py-6">
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">{item.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── CORE VALUES — heading from top, table from left ── */}
      <section className="bg-white py-10 sm:py-14 lg:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">

          {/* Heading fades in from top */}
          <motion.div
            className="flex justify-center mb-8 sm:mb-10"
            initial={{ opacity: 0, y: -50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.6 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <h2 className="text-xl sm:text-4xl rounded-md font-extrabold text-white px-8 sm:px-12 py-3 tracking-widest" style={{ backgroundColor: "#0F1030" }}>
              OUR CORE VALUES
            </h2>
          </motion.div>

          {/* Cards grid — 2 cols, each card slides up with stagger */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {coreValues.map((row, i) => (
              <motion.div
                key={row.value}
                className="flex items-start gap-4 p-5 sm:p-6"
                style={{ backgroundColor: i % 2 === 0 ? "#0F1030" : "#0F1030", border: "2px solid #00C7EF" }}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 0.5, ease: "easeOut", delay: i * 0.1 }}
              >
                {/* Number badge */}
                <div
                  className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-extrabold text-base sm:text-lg"
                  style={{ backgroundColor: "#00C7EF", color: "#0F1030" }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <p className="font-extrabold text-sm sm:text-base mb-1" style={{ color: "#00C7EF" }}>
                    {row.value}
                  </p>
                  <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{row.means}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BRAND PILLARS ── */}
      <BrandPillars />

      {/* ── COMPANY OVERVIEW — image from left, text from right ── */}
      <section className="py-10 sm:py-14 lg:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">

          {/* Heading — drops in from top, same style as Core Values */}
          <motion.div
            className="flex justify-center mb-8 sm:mb-10"
            initial={{ opacity: 0, y: -50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.6 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <h2 className="text-xl sm:text-4xl rounded-md font-extrabold text-white px-8 sm:px-12 py-3 tracking-widest" style={{ backgroundColor: "#0F1030" }}>
              WHO WE ARE
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 overflow-hidden">
            <motion.div
              className="h-64 sm:h-auto"
              initial={{ opacity: 0, x: -80 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            >
              <img src={serviceMan} alt="SmartCool Technician" className="w-full h-full object-cover" />
            </motion.div>
            <motion.div
              className="text-white px-6 sm:px-8 py-8 sm:py-10 flex flex-col justify-center"
              style={{ backgroundColor: "#0F1030", borderLeft: "2px solid #0F1030" }}
              initial={{ opacity: 0, x: 80 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 }}
            >
              <p className="text-sm sm:text-base leading-relaxed mb-5">
                <span className="font-extrabold">SMARTCOOL AIR CONDITIONING CO.</span>{" "}
                SMC LTD is a vertically integrated and fully registered Ugandan company headquartered in Kampala, providing world-class Heating, Ventilation, and Air Conditioning (HVAC) services across Uganda. We are a true full-service HVAC company — covering{" "}
                <span className="font-medium">design, equipment supply, installation, maintenance, energy auditing, and technical consultancy</span>{" "}
                all under one roof.
              </p>
              <p className="text-sm sm:text-base leading-relaxed mb-5">
                What sets SMARTCOOL apart is the caliber of expertise at our foundation. The company is led by a qualified Team of Engineers with extensive hand-on industry experience. This rare convergence of technical rigour and real-world field experience means every SMARTCOOL project is approached with engineering principle and practical intelligence.
              </p>
              <p className="text-sm sm:text-base leading-relaxed">
                Our multi-brand portfolio covering the world's leading HVAC manufacturers —{" "}
                <span className="underline font-medium">Hisense, LG, Samsung, Trane, Gree, Midea, Chiq</span>{" "}
                and others, ensures we always match the right technology to every client's specific needs and budget. From a single split unit for a private home to a centralized system for a major hospital, SMARTCOOL delivers with the same professionalism and commitment to excellence.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── OUR PROMISE — heading from top, items stagger up one by one, image fades in ── */}
      <section className="bg-white py-10 sm:py-14 lg:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">

          <motion.h2
            className="text-2xl sm:text-3xl font-extrabold mb-1"
            style={{ color: "#0F1030" }}
            initial={{ opacity: 0, y: -40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.6 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            Our Promise to Clients
          </motion.h2>
          <motion.div
            className="w-16 h-0.5 mb-4"
            style={{ backgroundColor: "#0F1030", originX: 0 }}
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            viewport={{ once: false, amount: 0.6 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
          />
          <motion.p
            className="text-gray-700 text-sm sm:text-base leading-relaxed mb-8 max-w-2xl"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: false, amount: 0.5 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.3 }}
          >
            Every SMARTCOOL project begins and ends with the client's best interest at heart. We do not upsell unnecessarily, we do not cut corners, and we do not walk away until our clients are fully satisfied.
          </motion.p>

          <div className="grid grid-cols-1 sm:grid-cols-2 overflow-hidden" style={{ border: "2px solid #0F1030" }}>

            {/* Left — each promise item staggers up one by one */}
            <div className="px-6 sm:px-8 py-8 space-y-5" style={{ backgroundColor: "#0F1030" }}>
              {promises.map((p, i) => (
                <motion.div
                  key={i}
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: false, amount: 0.6 }}
                  transition={{ duration: 0.5, ease: "easeOut", delay: i * 0.15 }}
                >
                  <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mt-0.5" style={{ backgroundColor: "#00C7EF" }}>
                    <svg className="w-4 h-4" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-white text-xs sm:text-sm leading-relaxed">{p}</p>
                </motion.div>
              ))}
              <motion.div
                className="pt-2"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.6 }}
                transition={{ duration: 0.5, ease: "easeOut", delay: promises.length * 0.15 }}
              >
                <Link
                  to="/contact"
                  className="inline-block text-white font-semibold px-5 py-2 rounded text-sm hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: "#00C7EF" }}
                >
                  Book Appointment
                </Link>
              </motion.div>
            </div>

            {/* Right — image fades in */}
            <motion.div
              className="h-64 sm:h-auto max-h-80 sm:max-h-96 overflow-hidden"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.9, ease: "easeOut", delay: 0.3 }}
            >
              <img src={whyImage} alt="SmartCool Promise" className="w-full h-full object-cover object-top" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── CTA — scales in from center ── */}
      <section className="py-10 sm:py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div
            className="rounded-xl py-12 sm:py-16 px-6 text-center"
            style={{ backgroundColor: "#0F1030", border: "2px solid #00C7EF" }}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: false, amount: 0.4 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <motion.h2
              className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-6"
              style={{ color: "#00C7EF" }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.4 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
            >
              Ready to Experience True Comfort?
            </motion.h2>
            <motion.a
              href="tel:+256200937979"
              className="inline-block text-white font-bold px-8 py-3 rounded-lg text-sm sm:text-base hover:opacity-90 transition-opacity"
              style={{ backgroundColor: "#00C7EF" }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.4 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.35 }}
            >
              Give Us A Call
            </motion.a>
          </motion.div>
        </div>
      </section>

    </motion.div>
  );
}